import { Operation } from '../hooks/useOperations/types';

/**
 * A storage value is only safe to persist when it is a non-empty string.
 * Guards against writing `undefined`, `null`, `''`, or the string `"undefined"`
 * into session storage / cookies.
 */
export const isValidStorageValue = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0 && value !== 'undefined' && value !== 'null';

/**
 * An operation is valid only when it exists and carries a non-empty operationId.
 * This is the guard that prevents `undefined`/`null` entries (e.g. `operations[1]`
 * when a user has no real operations) from leaking into selection state.
 */
export const isValidOperation = (operation: Operation | null | undefined): operation is Operation =>
  operation != null && typeof operation.operationId === 'string' && operation.operationId.length > 0;

/**
 * Removes any falsy / malformed entries from a list of operations, always
 * returning a real array (never `undefined`).
 */
export const filterValidOperations = (
  operations: readonly (Operation | null | undefined)[] | null | undefined,
): Operation[] => (Array.isArray(operations) ? operations.filter(isValidOperation) : []);

/**
 * Safe wrapper around a `setOperationsSelected` state setter. Filters out any
 * falsy/invalid entries before committing, so the selection can never contain
 * `undefined` (which would crash downstream plugin containers).
 */
export const commitOperationsSelected = (
  setOperationsSelected: (operations: Operation[]) => void,
  operations: readonly (Operation | null | undefined)[] | null | undefined,
): void => {
  setOperationsSelected(filterValidOperations(operations));
};
